import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import axios from 'axios';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewer.css';

// Configure PDF.js worker
// Use local worker file from public folder (most reliable)
if (typeof window !== 'undefined') {
    // Use local worker file (copied to public folder)
    // This avoids CDN issues and works offline
    const publicUrl = process.env.PUBLIC_URL || '';
    pdfjs.GlobalWorkerOptions.workerSrc = `${publicUrl}/pdf.worker.min.mjs`;
}

interface PdfViewerProps {
    url: string;
    className?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ url, className = '' }) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [scale, setScale] = useState<number>(1.0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
    const [documentReady, setDocumentReady] = useState<boolean>(false);
    const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set());
    const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const observerRef = useRef<IntersectionObserver | null>(null);
    
    // Configuration for lazy loading
    const PAGE_BUFFER = 2; // Number of pages to render above/below viewport
    
    // Fetch PDF with authentication
    useEffect(() => {
        const fetchPdf = async () => {
            try {
                setLoading(true);
                setError(null);
                setDocumentReady(false);
                setNumPages(null);
                
                const token = localStorage.getItem('token');
                const response = await axios.get(url, {
                    responseType: 'arraybuffer',
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                    },
                });
                
                // Create a Blob from the ArrayBuffer to avoid detachment issues
                // Using Blob URL instead of passing ArrayBuffer directly prevents worker issues
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);
                setPdfBlobUrl(blobUrl);
                setLoading(false);
            } catch (err: any) {
                console.error('Error fetching PDF:', err);
                setError(err.response?.status === 401 
                    ? new Error('Authentication required. Please log in again.')
                    : new Error(err.message || 'Failed to load PDF'));
                setLoading(false);
            }
        };
        
        if (url) {
            fetchPdf();
        }
        
        // Cleanup: revoke blob URL on unmount or when URL changes
        return () => {
            setPdfBlobUrl((prevUrl) => {
                if (prevUrl) {
                    URL.revokeObjectURL(prevUrl);
                }
                return null;
            });
            setDocumentReady(false);
            setNumPages(null);
            setVisiblePages(new Set());
            pageRefs.current.clear();
        };
    }, [url]);

    // Initialize visible pages when document loads
    useEffect(() => {
        if (documentReady && numPages) {
            // Initially load first few pages
            const initialPages = new Set<number>();
            for (let i = 1; i <= Math.min(5, numPages); i++) {
                initialPages.add(i);
            }
            setVisiblePages(initialPages);
        }
    }, [documentReady, numPages]);

    // Set up Intersection Observer for lazy loading pages
    useEffect(() => {
        if (!documentReady || !numPages) return;

        // Clean up existing observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Create Intersection Observer to detect visible pages
        observerRef.current = new IntersectionObserver(
            (entries) => {
                setVisiblePages((prevVisible) => {
                    const newVisiblePages = new Set(prevVisible);
                    
                    entries.forEach((entry) => {
                        const pageNumber = parseInt(entry.target.getAttribute('data-page-number') || '0', 10);
                        if (pageNumber > 0 && pageNumber <= numPages) {
                            if (entry.isIntersecting) {
                                // Add visible page and buffer pages
                                for (let i = Math.max(1, pageNumber - PAGE_BUFFER); 
                                     i <= Math.min(numPages, pageNumber + PAGE_BUFFER); 
                                     i++) {
                                    newVisiblePages.add(i);
                                }
                            } else {
                                // Remove pages that are far from viewport
                                const rect = entry.boundingClientRect;
                                const windowHeight = window.innerHeight;
                                
                                // Only remove if page is significantly outside viewport (2x window height)
                                if (rect.bottom < -windowHeight * 2 || rect.top > windowHeight * 3) {
                                    newVisiblePages.delete(pageNumber);
                                }
                            }
                        }
                    });
                    
                    return newVisiblePages;
                });
            },
            {
                root: null,
                rootMargin: '200px', // Start loading pages 200px before they enter viewport
                threshold: 0.01
            }
        );

        // Observe all existing page wrappers
        const observePages = () => {
            pageRefs.current.forEach((ref) => {
                if (ref && observerRef.current) {
                    observerRef.current.observe(ref);
                }
            });
        };

        // Observe pages after a short delay to ensure DOM is ready
        const timeoutId = setTimeout(observePages, 100);

        return () => {
            clearTimeout(timeoutId);
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [documentReady, numPages, PAGE_BUFFER]);


    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        setNumPages(numPages);
        // Small delay to ensure worker is fully initialized
        setTimeout(() => {
            setDocumentReady(true);
            setLoading(false);
        }, 100);
        setError(null);
    }

    function onDocumentLoadError(error: Error): void {
        console.error('Error loading PDF:', error);
        setError(error);
        setLoading(false);
        setDocumentReady(false);
    }

    function zoomIn(): void {
        setScale((prev) => Math.min(prev + 0.25, 3.0));
    }

    function zoomOut(): void {
        setScale((prev) => Math.max(prev - 0.25, 0.5));
    }

    function fitToWidth(): void {
        // Reset to default scale
        setScale(1.0);
    }

    if (error) {
        return (
            <div className={`pdf-viewer-error ${className}`}>
                <div className="pdf-viewer-error-content">
                    <h3>Error Loading PDF</h3>
                    <p>{error.message || 'Failed to load PDF document'}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className={`pdf-viewer-container ${className}`}>
            {/* PDF Controls */}
            <div className="pdf-viewer-controls">
                <div className="pdf-viewer-controls-left">
                    <span className="pdf-viewer-page-info">
                        {numPages ? `${numPages} page${numPages !== 1 ? 's' : ''}` : 'Loading...'}
                    </span>
                </div>
                <div className="pdf-viewer-controls-right">
                    <button
                        onClick={zoomOut}
                        disabled={scale <= 0.5}
                        className="pdf-viewer-button"
                        title="Zoom out"
                    >
                        <ZoomOut size={18} />
                    </button>
                    <span className="pdf-viewer-zoom-info">
                        {Math.round(scale * 100)}%
                    </span>
                    <button
                        onClick={zoomIn}
                        disabled={scale >= 3.0}
                        className="pdf-viewer-button"
                        title="Zoom in"
                    >
                        <ZoomIn size={18} />
                    </button>
                    <button
                        onClick={fitToWidth}
                        className="pdf-viewer-button"
                        title="Reset zoom"
                    >
                        <Maximize2 size={18} />
                    </button>
                </div>
            </div>

            {/* PDF Document */}
            <div className="pdf-viewer-content">
                {loading && (
                    <div className="pdf-viewer-loading">
                        <div className="pdf-viewer-spinner"></div>
                        <p>Loading PDF...</p>
                    </div>
                )}
                {pdfBlobUrl && !error && (
                    <Document
                        file={pdfBlobUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={
                            <div className="pdf-viewer-loading">
                                <div className="pdf-viewer-spinner"></div>
                                <p>Loading PDF...</p>
                            </div>
                        }
                        className="pdf-viewer-document"
                    >
                        {documentReady && numPages && numPages > 0 && (
                            <div className="pdf-viewer-pages-container">
                                {Array.from(new Array(numPages), (el, index) => {
                                    const pageNumber = index + 1;
                                    const isVisible = visiblePages.has(pageNumber);
                                    
                                    return (
                                        <div 
                                            key={`page_${pageNumber}`} 
                                            className="pdf-viewer-page-wrapper"
                                            data-page-number={pageNumber}
                                            ref={(el) => {
                                                if (el) {
                                                    pageRefs.current.set(pageNumber, el);
                                                    // Observe the element when it's created
                                                    if (observerRef.current) {
                                                        observerRef.current.observe(el);
                                                    }
                                                } else {
                                                    pageRefs.current.delete(pageNumber);
                                                }
                                            }}
                                        >
                                            {isVisible ? (
                                                <Page
                                                    pageNumber={pageNumber}
                                                    scale={scale}
                                                    className="pdf-viewer-page"
                                                    renderTextLayer={true}
                                                    renderAnnotationLayer={true}
                                                    loading={
                                                        <div className="pdf-viewer-page-loading">
                                                            <div className="pdf-viewer-spinner"></div>
                                                        </div>
                                                    }
                                                />
                                            ) : (
                                                <div 
                                                    className="pdf-viewer-page-placeholder"
                                                    style={{ 
                                                        minHeight: '800px', 
                                                        width: '100%',
                                                        backgroundColor: '#f3f4f6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#6b7280'
                                                    }}
                                                >
                                                    Page {pageNumber}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Document>
                )}
            </div>
        </div>
    );
};

export default PdfViewer;

